import { orderTypeConstants } from "constants/order";
import { centsToDollars, parsePrice } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { useCallback, useMemo, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IPaymentPlan } from "types/Payment";

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(4)};
  display: "block";
  .p-card-subtitle {
    color: var(--orange-500);
  }
  .p-card-body {
    padding-left: 0px !important;
  }
`;

const DOWNPAYMENT_ONLY_TYPE_OPTIONS = ["Percentage", "Flat amount"];

export function DownpaymentOnlySection({
  downpaymentOnlyPaymentPlan,
  isRedo,
  isDraft,
  isCopy,
}: {
  downpaymentOnlyPaymentPlan?: IPaymentPlan;
  isRedo?: boolean;
  isDraft?: boolean;
  isCopy?: boolean;
}) {
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();

  const downpaymentOnlyTypeField = watch("downpaymentOnlyType");
  const downpaymentOnlyFlatAmountField = watch("downpaymentOnlyFlatAmount");
  const totalAmountField = watch("totalAmount");
  const amountField = watch("amount");
  const orderTypeField = watch("orderType");
  const clearPatientFeePercentageField = watch("clearPatientFeePercentage"); // Clear Patient Fee percentage
  const initialClearPatientFeeValueInPercentage = watch(
    "initialClearPatientFeeValueInPercentage"
  ); // Original order's clear patient fee (for redo)

  const downpaymentOnlyMaxAmount = useMemo(() => {
    if (orderTypeField === orderTypeConstants.GFE) {
      return amountField || 0;
    }
    return totalAmountField;
  }, [orderTypeField, amountField, totalAmountField]);

  const isNoBalanceDueOrder = useMemo(() => {
    if (orderTypeField === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      return amountField === 0;
    }
    return totalAmountField === 0;
  }, [orderTypeField, amountField, totalAmountField]);

  const showPercentageField = useMemo(() => {
    return downpaymentOnlyTypeField === "Percentage";
  }, [downpaymentOnlyTypeField]);

  const downpaymentOnlyFlatAmountWithFees = useMemo(() => {
    if (
      !downpaymentOnlyFlatAmountField ||
      downpaymentOnlyFlatAmountField <= 0 ||
      !clearPatientFeePercentageField ||
      clearPatientFeePercentageField <= 0
    ) {
      return null;
    }
    const clearPatientFeeAmount =
      downpaymentOnlyFlatAmountField * (clearPatientFeePercentageField / 100);
    const totalWithFees =
      downpaymentOnlyFlatAmountField + clearPatientFeeAmount;

    return totalWithFees;
  }, [downpaymentOnlyFlatAmountField, clearPatientFeePercentageField]);

  useEffect(() => {
    if (downpaymentOnlyFlatAmountWithFees) {
      setValue(
        "downpaymentOnlyFlatAmountWithFees",
        downpaymentOnlyFlatAmountWithFees
      );
    } else {
      setValue("downpaymentOnlyFlatAmountWithFees", null);
    }
  }, [downpaymentOnlyFlatAmountWithFees, setValue]);

  useEffect(() => {
    trigger("downpaymentOnlyFlatAmount");
    trigger("downpaymentOnlyPercent");
  }, [downpaymentOnlyMaxAmount, downpaymentOnlyTypeField, trigger]);

  const handleChangeDownPaymentOnlyType = (e: { value: string }) => {
    setValue("downpaymentOnlyType", e.value);
    if (e.value === "Percentage") {
      setValue("downpaymentOnlyFlatAmount", null);
    } else {
      setValue("downpaymentOnlyPercent", null);
    }
  };

  const resetFields = useCallback(() => {
    if (isRedo && downpaymentOnlyPaymentPlan) {
      setValue(
        "downpaymentOnlyType",
        downpaymentOnlyPaymentPlan?.paymentPlanMinDownPaymentPercent
          ? "Percentage"
          : "Flat amount"
      );
      setValue(
        "downpaymentOnlyPercent",
        downpaymentOnlyPaymentPlan?.paymentPlanMinDownPaymentPercent || null
      );
      // Calculate flat amount with clear patient fee adjustment for GFE orders
      let flatAmountValue = null;
      if (
        downpaymentOnlyPaymentPlan?.paymentPlanMinDownPaymentFlat !== null &&
        downpaymentOnlyPaymentPlan?.paymentPlanMinDownPaymentFlat !== undefined
      ) {
        const valueInDollars = centsToDollars(
          downpaymentOnlyPaymentPlan.paymentPlanMinDownPaymentFlat
        );
        // Downgrade value if order has clear patient fee (for GFE orders)
        if (
          orderTypeField === orderTypeConstants.GFE &&
          initialClearPatientFeeValueInPercentage
        ) {
          flatAmountValue =
            valueInDollars /
            (1 + initialClearPatientFeeValueInPercentage / 100);
        } else {
          flatAmountValue = valueInDollars;
        }
      }
      setValue("downpaymentOnlyFlatAmount", flatAmountValue);
      // Trigger validation after setting values
      trigger("partialPaymentPercent");
      trigger("partialPaymentFlatAmount");
      return;
    }
    setValue("downpaymentOnlyType", "Flat amount");
    setValue("downpaymentOnlyPercent", null);
    setValue("downpaymentOnlyFlatAmount", null);
    // Trigger validation to mark fields as invalid when null
    trigger("downpaymentOnlyPercent");
    trigger("downpaymentOnlyFlatAmount");
  }, [
    setValue,
    trigger,
    isRedo,
    downpaymentOnlyPaymentPlan,
    orderTypeField,
    initialClearPatientFeeValueInPercentage,
  ]);

  useEffect(() => {
    if (isCopy || isDraft) {
      return;
    }
    resetFields();
  }, []);
  return (
    <StyledCard data-testid="partialPayment_Card">
      <div className="flex flex-column gap-4 w-full">
        <div className="formgrid grid w-full">
          <div className="field flex flex-column col-12 lg:col-4 xl:col-3">
            <Label
              htmlFor="downpaymentOnlyType"
              data-testid="downpaymentOnlyTypeLabel"
            >
              Downpayment Only Type
            </Label>
            <Controller
              name="downpaymentOnlyType"
              control={control}
              defaultValue="Percentage"
              render={({ field: { value } }) => (
                <Dropdown
                  id="downpaymentOnlyType"
                  data-testid="downpaymentOnlyType_Dropdown"
                  value={value}
                  onChange={handleChangeDownPaymentOnlyType}
                  options={DOWNPAYMENT_ONLY_TYPE_OPTIONS}
                  placeholder="Select downpayment only type"
                  className="w-full"
                />
              )}
            />
          </div>
          <div
            className={`field flex-column col-12 lg:col-4 xl:col-3 ${
              showPercentageField ? "flex" : "hidden"
            }`}
          >
            <Label
              htmlFor="downpaymentOnlyPercent"
              data-testid="downpaymentOnlyPercentLabel"
            >
              Payment Percent
            </Label>
            <Controller
              name="downpaymentOnlyPercent"
              control={control}
              rules={{
                required: showPercentageField
                  ? "Payment percent is required"
                  : false,
                validate: () => {
                  if (showPercentageField && isNoBalanceDueOrder) {
                    return "Downpayment can not be created for No Balance Due orders";
                  }
                  return true;
                },
                min: {
                  value: 1,
                  message: "Percent must be at least 1%",
                },
                max: {
                  value: 100,
                  message: "Percent cannot exceed 100%",
                },
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <InputNumber
                    value={value}
                    suffix="%"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "downpaymentOnlyPercent",
                        trigger,
                      })
                    }
                    inputId="downpaymentOnlyPercent"
                    data-testid="downpaymentOnlyPercent_Input"
                    onValueChange={onChange}
                    min={1}
                    max={100}
                    placeholder="Enter percentage"
                  />
                  {getFormErrorMessage("downpaymentOnlyPercent", errors)}
                </>
              )}
            />
          </div>
          <div
            className={`field flex-column col-12 lg:col-4 xl:col-3 ${
              showPercentageField ? "hidden" : "flex"
            }`}
          >
            <Label
              htmlFor="downpaymentOnlyFlatAmount"
              data-testid="downpaymentOnlyFlatAmountLabel"
            >
              Amount
            </Label>
            <Controller
              name="downpaymentOnlyFlatAmount"
              control={control}
              rules={{
                required: !showPercentageField ? "Amount is required" : false,
                validate: () => {
                  if (!showPercentageField && isNoBalanceDueOrder) {
                    return "Downpayment can not be created for No Balance Due orders";
                  }
                  return true;
                },
                min: {
                  value: 1,
                  message: "Amount must be at least $1",
                },
                max: {
                  value: downpaymentOnlyMaxAmount,
                  message: isNoBalanceDueOrder
                    ? "Downpayment can not be created for No Balance Due orders"
                    : `Amount cannot exceed the total amount ${parsePrice(
                        downpaymentOnlyMaxAmount
                      )}`,
                },
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <InputNumber
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "downpaymentOnlyFlatAmount",
                        trigger,
                      })
                    }
                    inputId="downpaymentOnlyFlatAmount"
                    data-testid="downpaymentOnlyFlatAmount_Input"
                    onValueChange={onChange}
                    min={1}
                    max={
                      downpaymentOnlyMaxAmount > 0
                        ? downpaymentOnlyMaxAmount
                        : undefined
                    }
                    placeholder="Enter amount"
                  />
                  {downpaymentOnlyFlatAmountWithFees && (
                    <div className="mt-2">
                      <p className="text-sm mb-1 text-primary">
                        with fees:{" "}
                        {parsePrice(downpaymentOnlyFlatAmountWithFees)}
                      </p>
                    </div>
                  )}
                  {getFormErrorMessage("downpaymentOnlyFlatAmount", errors)}
                </>
              )}
            />
          </div>
        </div>
      </div>
    </StyledCard>
  );
}
